import { Fragment, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FedexResolvedAddress } from "@/types/api/api-types";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { cn } from "@/lib/utils";
import {
  buildFedexAddressComparisonRows,
  ComparableAddressSnapshot,
} from "./fedexAddressValidation.utils";

export const FedexAddressValidationComparisonModal = ({
  open,
  onClose,
  title,
  treatCountryAsFixed,
  showStateOrProvince = true,
  original,
  resolved,
  onKeepOriginal,
  onUseSuggestion,
  countrySupported,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  treatCountryAsFixed?: boolean;
  showStateOrProvince?: boolean;
  original: ComparableAddressSnapshot;
  resolved: FedexResolvedAddress;
  onKeepOriginal: () => void;
  onUseSuggestion: () => void;
  countrySupported: boolean;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const translateMessage = useTranslateMessage();
  const rows = useMemo(
    () =>
      buildFedexAddressComparisonRows(original, resolved, treatCountryAsFixed, showStateOrProvince),
    [original, resolved, treatCountryAsFixed, showStateOrProvince],
  );

  const showUnmatchedMessage = resolved.attributes?.Resolved === "false";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={title}
      description={t("fedexAddressComparison.description")}
      contentClassName="max-h-[85vh]"
    >
      <div className="flex flex-col gap-4">
        {treatCountryAsFixed ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            {t("fedexAddressComparison.countryFixedNotice")}
          </p>
        ) : null}
        {showUnmatchedMessage ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t("fedexAddressComparison.unmatchedNotice")}
          </p>
        ) : null}

        {!countrySupported ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            {t("fedexAddressComparison.countryNotSupportedNotice")}
          </p>
        ) : null}

        <div className="rounded-md border border-gray-200 bg-white text-sm">
          {/* Compact layout: below md — avoids cramped 3-column table on small / medium widths */}
          <div className="flex flex-col gap-2 md:hidden">
            {rows.map((row) => (
              <div
                key={row.label}
                className="rounded-md border border-gray-100 bg-gray-50/80 px-3 py-2.5 shadow-sm"
              >
                <div className="text-xs font-semibold text-gray-700">
                  {translateMessage(row.label)}
                </div>
                <div className="mt-2 grid gap-1.5 text-xs">
                  <div>
                    <span className="text-gray-500">
                      {t("fedexAddressComparison.originalInlineLabel")}
                    </span>
                    <span className="text-gray-900">{row.original}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">
                      {t("fedexAddressComparison.suggestedInlineLabel")}
                    </span>
                    <span
                      className={cn(
                        row.highlightSuggested ? "font-medium text-red-600" : "text-gray-900",
                      )}
                    >
                      {row.suggested}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:justify-stretch">
              <Button
                type="button"
                variant="outline"
                className="w-full flex-1"
                onClick={onKeepOriginal}
              >
                {t("fedexAddressComparison.keepOriginal")}
              </Button>
              <Button type="button" className="w-full flex-1" onClick={onUseSuggestion}>
                {t("fedexAddressComparison.useSuggestion")}
              </Button>
            </div>
          </div>

          {/* Wide 3-column table: md and up */}
          <div className="hidden grid-cols-[minmax(0,0.9fr)_1fr_1fr] gap-px bg-gray-200 md:grid">
            <div className="bg-violet-700 px-3 py-2 text-xs font-semibold text-white sm:text-sm" />
            <div className="bg-violet-700 px-3 py-2 text-center text-xs font-semibold text-white sm:text-sm">
              {t("fedexAddressComparison.originalColumn")}
            </div>
            <div className="bg-violet-700 px-3 py-2 text-center text-xs font-semibold text-white sm:text-sm">
              {t("fedexAddressComparison.suggestedColumn")}
            </div>
            {rows.map((row) => (
              <Fragment key={row.label}>
                <div className="bg-gray-50 px-3 py-2 font-medium text-gray-700">
                  {translateMessage(row.label)}
                </div>
                <div className="bg-white px-3 py-2 text-gray-900">{row.original}</div>
                <div
                  className={cn(
                    "bg-white px-3 py-2",
                    row.highlightSuggested ? "font-medium text-red-600" : "text-gray-900",
                  )}
                >
                  {row.suggested}
                </div>
              </Fragment>
            ))}
            <div className="bg-gray-50" />
            <div className="flex justify-center bg-white px-2 py-3">
              <Button
                type="button"
                variant="outline"
                className="w-full max-w-[220px]"
                onClick={onKeepOriginal}
              >
                {t("fedexAddressComparison.keepOriginal")}
              </Button>
            </div>
            <div className="flex justify-center bg-white px-2 py-3">
              <Button type="button" className="w-full max-w-[220px]" onClick={onUseSuggestion}>
                {t("fedexAddressComparison.useSuggestion")}
              </Button>
            </div>
          </div>
        </div>

        <p className="text-muted-foreground text-xs sm:text-sm">
          {t("fedexAddressComparison.disclaimer")}
        </p>
      </div>
    </Modal>
  );
};
